import { Link } from "react-router-dom";
import {
  Eye, ScanEye, Baby, Droplets, Sparkles, ArrowRight, Star, QrCode,
  Stethoscope, Microscope, HeartHandshake, Award, MapPin, Navigation, Phone, Mail, Clock, MessageCircle,
} from "lucide-react";
import { Reveal, Counter } from "./motion";
import { SectionHead } from "./SectionHead";
import { PhotoSlot, slug } from "./PhotoSlot";
import { AppointmentForm } from "./AppointmentForm";
import {
  CLINIC, SPECIALTIES_PRIMARY, SPECIALTIES_OTHER, WHY_CHOOSE, JOURNEY_STEPS, GALLERY_ITEMS, whatsappLink,
} from "../lib/constants";

const SPEC_ICONS = [ScanEye, Sparkles, Droplets, Baby, Eye];

export const Specialties = () => (
  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
    {SPECIALTIES_PRIMARY.map((s, i) => {
      const Icon = SPEC_ICONS[i % SPEC_ICONS.length];
      return (
        <Reveal key={s.title} delay={i * 0.08}>
          <div
            className={`card-lift h-full rounded-2xl border p-7 ${
              i === 0 ? "bg-ink text-white border-ink noise-dark" : "bg-white border-slate-200"
            }`}
            data-testid={`specialty-card-${slug(s.title)}`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${i === 0 ? "bg-white/10" : "bg-brand-mist"}`}>
              <Icon className={`w-6 h-6 ${i === 0 ? "text-aqua-bright" : "text-brand-bright"}`} strokeWidth={1.6} aria-hidden="true" />
            </div>
            <h3 className={`mt-5 text-lg font-extrabold ${i === 0 ? "text-white" : "text-ink"}`}>{s.title}</h3>
            <p className={`mt-2 text-sm leading-relaxed ${i === 0 ? "text-blue-100/80" : "text-slate-600"}`}>{s.desc}</p>
          </div>
        </Reveal>
      );
    })}
    <Reveal delay={0.4}>
      <Link
        to="/services"
        className="card-lift h-full min-h-[180px] rounded-2xl border-2 border-dashed border-ink/20 bg-brand-mist/40 p-7 flex flex-col justify-center group"
        data-testid="specialty-card-view-all"
      >
        <p className="text-lg font-extrabold text-ink">Complete Eye Care</p>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          Oculoplasty, Squint, Cornea, Paediatric Ophthalmology, Glaucoma and more.
        </p>
        <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-brand-bright group-hover:gap-3 transition-[gap]">
          View all services <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </span>
      </Link>
    </Reveal>
  </div>
);

const OTHER_ICONS = [Eye, Eye, ScanEye, Baby, Droplets, Eye];

export const OtherServices = () => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {SPECIALTIES_OTHER.map((s, i) => (
      <Reveal key={s.title} delay={i * 0.06}>
        <div className="card-lift h-full rounded-2xl border border-slate-200 bg-white p-6 flex gap-4" data-testid={`other-service-${slug(s.title)}`}>
          <div className="w-11 h-11 shrink-0 rounded-xl bg-aqua-mist flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-aqua" strokeWidth={1.7} aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-base font-extrabold text-ink">{s.title}</h3>
            <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
          </div>
        </div>
      </Reveal>
    ))}
  </div>
);

const WHY_ICONS = [Award, Sparkles, ScanEye, Microscope, Microscope, Eye, HeartHandshake, MapPin];

export const WhyChoose = () => (
  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
    {WHY_CHOOSE.map((w, i) => {
      const Icon = WHY_ICONS[i % WHY_ICONS.length];
      return (
        <Reveal key={w.title} delay={i * 0.06}>
          <div className="card-lift h-full rounded-2xl border border-slate-200 bg-white p-6" data-testid={`why-card-${i}`}>
            <Icon className="w-6 h-6 text-aqua" strokeWidth={1.6} aria-hidden="true" />
            <h3 className="mt-4 text-[15px] font-extrabold text-ink leading-snug">{w.title}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.desc}</p>
          </div>
        </Reveal>
      );
    })}
  </div>
);

export const PatientJourney = () => (
  <ol className="relative grid gap-6 md:grid-cols-5 md:gap-4">
    {JOURNEY_STEPS.map((s, i) => (
      <Reveal key={s.title} delay={i * 0.1}>
        <li className="relative h-full rounded-2xl border border-slate-200 bg-white p-6 card-lift" data-testid={`journey-step-${i + 1}`}>
          <span className="font-serif italic text-4xl text-brand-bright/25">{String(i + 1).padStart(2, "0")}</span>
          <h3 className="mt-3 text-[15px] font-extrabold text-ink leading-snug">{s.title}</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
        </li>
      </Reveal>
    ))}
  </ol>
);

export const ReviewOffer = ({ compact = false }) => (
  <div className="relative overflow-hidden rounded-3xl bg-ink noise-dark text-white" data-testid="review-offer-section">
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ background: "radial-gradient(80% 90% at 85% 10%, rgba(6,182,212,0.22), transparent 60%)" }}
    />
    <div className={`relative grid gap-10 lg:grid-cols-[1.2fr_auto] items-center ${compact ? "p-8 sm:p-10" : "p-8 sm:p-14"}`}>
      <div>
        <span className="kicker !text-aqua-bright">Google Reviews</span>
        <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
          Review us on Google & get <span className="font-serif italic font-semibold text-aqua-bright">20% off</span> on consultation
        </h2>
        <p className="mt-4 text-blue-100/80 text-base sm:text-lg leading-relaxed max-w-xl">
          Share your experience with iNetra on Google and receive 20% off on consultation. Your feedback helps other
          patients in Delhi find specialist retina and cataract care.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <a
            href={CLINIC.reviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary !bg-aqua hover:!bg-aqua-bright"
            data-testid="google-review-cta"
          >
            <Star className="w-4 h-4" aria-hidden="true" /> Review Us on Google
          </a>
          <p className="text-xs text-blue-200/60">Opens our verified Google profile in a new tab.</p>
        </div>
      </div>
      <div className="justify-self-center text-center">
        <div className="bg-white rounded-2xl p-4 shadow-2xl shadow-black/30 inline-block">
          <img
            src="/images/google-review-qr.png"
            alt="QR code linking to the iNetra Google review page"
            className="w-44 h-44 sm:w-52 sm:h-52"
            width="208"
            height="208"
            data-testid="google-review-qr"
          />
        </div>
        <p className="mt-4 text-xs font-semibold text-blue-100/80 flex items-center justify-center gap-1.5">
          <QrCode className="w-3.5 h-3.5 text-aqua-bright" aria-hidden="true" /> Scan to open our Google review page
        </p>
      </div>
    </div>
  </div>
);

export const BookSection = () => (
  <section id="book" className="py-20 sm:py-28 bg-slate-50" data-testid="book-appointment-section">
    <div className="container-x">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.15fr] items-start">
        <div className="lg:sticky lg:top-28">
          <SectionHead
            chapter="10"
            kicker="Appointments"
            title={<>Ready to take care of <span className="font-serif italic font-semibold text-brand-bright">your eyes?</span></>}
            sub="Book your consultation at iNetra Laser Eye & Advanced Retina Centre. No advance payment — our team confirms every appointment personally."
          />
          <Reveal delay={0.15} className="mt-8 space-y-3">
            <a href={CLINIC.phoneLink} className="card-lift flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="book-call-card">
              <span className="w-11 h-11 rounded-xl bg-ink text-white flex items-center justify-center"><Phone className="w-5 h-5" aria-hidden="true" /></span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-wider text-slate-500">Call Now</span>
                <span className="block text-base font-extrabold text-ink">{CLINIC.phoneDisplay}</span>
              </span>
            </a>
            <a href={whatsappLink("Hello iNetra, I would like to book an appointment.")} target="_blank" rel="noopener noreferrer" className="card-lift flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="book-whatsapp-card">
              <span className="w-11 h-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center"><MessageCircle className="w-5 h-5" aria-hidden="true" /></span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-wider text-slate-500">WhatsApp</span>
                <span className="block text-base font-extrabold text-ink">{CLINIC.phoneDisplay}</span>
              </span>
            </a>
            <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="book-hours-card">
              <span className="w-11 h-11 rounded-xl bg-brand-mist flex items-center justify-center"><Clock className="w-5 h-5 text-brand-bright" aria-hidden="true" /></span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-wider text-slate-500">Open 7 Days</span>
                <span className="block text-sm font-bold text-ink">Mon–Sat 9 AM – 9 PM · Sun 10 AM – 3 PM</span>
              </span>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-xl shadow-blue-900/5">
            <AppointmentForm />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const MapSection = () => (
  <section className="py-20 sm:py-28" data-testid="location-section">
    <div className="container-x">
      <div className="grid gap-10 lg:grid-cols-2 items-stretch">
        <div>
          <SectionHead
            chapter="11"
            kicker="Visit Us"
            title="Find us in Janak Puri"
            sub="Easy to reach, opposite District Centre. Open 7 days a week."
          />
          <Reveal delay={0.15} className="mt-8 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-aqua" aria-hidden="true" /> Address
              </p>
              <p className="mt-2 text-base font-bold text-ink">{CLINIC.fullName}</p>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                {CLINIC.addressLines.join(" ")}
              </p>
              <p className="mt-1 text-xs text-slate-500">Landmark: {CLINIC.landmark}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={CLINIC.directionsUrl} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="get-directions-button">
                <Navigation className="w-4 h-4" aria-hidden="true" /> Get Directions
              </a>
              <a href={CLINIC.emailLink} className="btn-outline" data-testid="email-button">
                <Mail className="w-4 h-4" aria-hidden="true" /> {CLINIC.email}
              </a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="min-h-[340px]">
          <div className="h-full min-h-[340px] rounded-3xl overflow-hidden border border-slate-200 shadow-xl shadow-blue-900/5">
            <iframe
              title="iNetra Laser Eye & Advanced Retina Centre on Google Maps — B1/516, Janak Puri, New Delhi"
              src={CLINIC.mapEmbed}
              className="w-full h-full min-h-[340px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="google-map-embed"
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const TrustStrip = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-100" data-testid="trust-strip">
    {[
      { node: <Counter to={10} suffix="+" className="text-3xl sm:text-4xl font-extrabold text-ink tracking-tight" />, label: "Years Experience" },
      { node: <Counter to={10000} suffix="+" className="text-3xl sm:text-4xl font-extrabold text-ink tracking-tight" />, label: "Eye Surgeries" },
      { node: <span className="text-3xl sm:text-4xl font-extrabold text-ink tracking-tight">AIIMS</span>, label: "Trained Specialist" },
      { node: <span className="text-3xl sm:text-4xl font-extrabold text-ink tracking-tight font-serif italic">Retina</span>, label: "Advanced Retina Care" },
    ].map((s, i) => (
      <div key={i} className="px-4 py-6 sm:py-8 text-center" data-testid={`trust-stat-${i}`}>
        {s.node}
        <p className="mt-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-wider text-slate-500">{s.label}</p>
      </div>
    ))}
  </div>
);

export { GALLERY_ITEMS };
